'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { useCallback, useMemo } from 'react';
import { TreeAccordion } from 'ui-components';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import {
  PRICE_RANGES,
  buildPriceBundle,
  getPriceController,
  resolvePriceLabel,
} from '../facetControllers';
import { NumericFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { PriceFacetProps } from './PriceFacet.types';

const getValueId = (v: NumericFlatFacetValue): string =>
  `price-${v.start}-${v.end}`;

export const PriceFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: PriceFacetProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const priceLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.filter-price-label')
      ?.value ?? 'Price';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.clear')?.value ??
    'Clear';

  const {
    optimisticValues,
    selectedIds,
    selectedCount,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({
    getController: getPriceController,
    fieldName: 'ec_price_filter',
    getValueId,
  });

  const priceBundle = useMemo(
    () => buildPriceBundle(searchValuesMicrocopy),
    [searchValuesMicrocopy]
  );

  const getLabel = useCallback(
    (start: number, end: number) => {
      const range = PRICE_RANGES.find(
        (r) => r.start === start && r.end === end
      );
      if (!range) return `${start}-${end}`;
      return resolvePriceLabel(priceBundle, range.bundleKey, range.label);
    },
    [priceBundle]
  );

  const isDark = variant === 'mobile';

  const treeItems = useMemo(
    () =>
      optimisticValues.map((v) => ({
        id: getValueId(v),
        label: getLabel(v.start, v.end),
        count: v.numberOfResults,
        disabled: v.numberOfResults === 0 && v.state !== 'selected',
      })),
    [optimisticValues, getLabel]
  );

  const content = (
    <TreeAccordion
      items={treeItems}
      selectionMode="multiple"
      selectedIds={selectedIds}
      onSelectMany={handleSelectMany}
      isDarkTheme={isDark}
      size={variant === 'sidebar' ? 'default' : 'sm'}
    />
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={priceLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={priceLabel}
      variant={variant}
      className={className}
      onClear={handleClear}
      clearLabel={clearLabel}
      showClear={variant === 'drawer' && selectedCount > 0}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
